import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";

import { IConfiguration } from "data/configuration";
import { getUser } from "data/user";

import { ACCESS_TOKEN, DEVICE_CODE } from "config";
import useGetConfiguration from "hooks/useGetConfiguration";
import rg4js from "raygun4js";

const MAX_CONFIGURATION_ATTEMPTS = 30; // 5 minutes based off refetch interval
const REFETCH_INTERVAL = 1000 * 10; // 10 seconds

const Admin = () => {
  const deviceCode = localStorage.getItem(DEVICE_CODE);
  const [accessTokenAttempts, setAccessTokenAttempts] = useState(0);

  const history = useHistory();
  const client = useQueryClient();

  const { isLoading: isFetchingUser } = useQuery("user", getUser, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      rg4js("setUser", {
        identifier: data.id,
        isAnonymous: false,
        email: data.email,
        firstName: data.first_name,
        fullName: `${data.first_name} ${data.last_name}`,
      });
    },
    onError: (e) => {
      rg4js("send", e);
    },
  });

  const onSuccess = (data: IConfiguration) => {
    if (data.message === null) {
      client.invalidateQueries("configuration");
      history.replace("/");
    } else {
      setAccessTokenAttempts((prev) => prev + 1);
    }
  };

  const onReset = () => {
    localStorage.removeItem(DEVICE_CODE);
    localStorage.removeItem(ACCESS_TOKEN);
    window.location.reload();
  };

  useGetConfiguration(
    onSuccess,
    null,
    REFETCH_INTERVAL,
    accessTokenAttempts < MAX_CONFIGURATION_ATTEMPTS
  );

  if (isFetchingUser) {
    return (
      <div className="container mx-auto">
        <p>Fetching user...</p>
      </div>
    );
  }

  if (accessTokenAttempts >= MAX_CONFIGURATION_ATTEMPTS) {
    return (
      <div className="h-screen flex items-center justify-center bg-pebbles">
        <div className="p-12 shadow-livly rounded-xl bg-white">
          <div className="mb-6 flex justify-center">
            <img src="/assets/logo.svg" alt="livly view" />
          </div>
          <div className="mb-12">
            <h2 className="mb-4">Activation Failed</h2>
            <p className="mb-4">
              We were unable to activate your device. Please ensure you have
              followed the activation steps correctly.
            </p>
            <p className="mb-4">
              If the problem persists, please contact support for further
              assistance.
            </p>
          </div>
          <div>
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded"
              onClick={onReset}
            >
              Restart Activation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex items-center justify-center bg-pebbles">
      <div className="p-12 shadow-livly rounded-xl bg-white">
        <div className="mb-6 flex justify-center">
          <img src="/assets/logo.svg" alt="livly view" />
        </div>
        <div className="mb-12">
          <h2 className="mb-4">
            Follow the directions below to finish the assignment your Livly
            View:
          </h2>
          <ol className="list-decimal text-3xl pl-4 space-y-4">
            <li>Visit hub.livly.io</li>
            <li>
              Click on the TV icon in the bottom left-hand corner of the Livly
              Hub Dashboard
            </li>
            <li>Navigate to the unassigned section at the top of the page</li>
            <li>
              Locate the ID number that matches the one displayed on this screen
            </li>
            <li>
              Click on the assign screen button and choose the property
              associated with this device
            </li>
            <li>Click save to finish the setup of your Livly View</li>
          </ol>
        </div>
        <div>
          <p className="mb-2 text-3xl">Screen ID:</p>
          <p className="font-bold text-7xl cursor-pointer">{deviceCode}</p>
        </div>
      </div>
    </div>
  );
};

export default Admin;
